import { useCallback, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import MightLike, { RECOMMENDATION_TYPES } from "../mightLike/MightLike";
import useRecommendations from "../mightLike/useRecommendations";
import { useRuntimeConstants } from "../../context/runtimeConstants/RuntimeConstantsContext";
import { PAGE_AREAS } from "../../constants";
import getDefaultCarouselSettings from "../../utils/getDefaultCarouselSettings";
import {
  Carousel,
  CarouselMode,
} from "@asosteam/asos-web-component-library-carousel";
import useGrid from "../grid/useGrid";
import Grid from "../grid/Grid";
import { buildProductTileAriaLabel } from "../productTile/buildProductTileAriaLabel";
import PriceThenTitle from "../productTile/PriceThenTitle";
import TitleThenPrice from "../productTile/TitleThenPrice";
import ProductTile from "../productTile/ProductTile";
import useDiscountPercentage from "../../hooks/useDiscountPercentage";
import { useViewport } from "../../context/viewport/ViewportContext";
import { useIsInStockContext } from "../../context/isInStock/IsInStockContext";
import getRecommendationsTitleAriaLabel from "../mightLike/getRecommendationsTitleAriaLabel";
import {
  setIntersectionObserver,
  unsetIntersectionObserver,
} from "../../utils/setIntersectionObserver";
import { fireMightLikeVisibleAnalytics } from "../../analytics/pageInteraction";
import { fireViewItemListMarketing } from "../../utils/marketingTracking/marketingTrackingEvents";

const INTERSECTION_OPTIONS = {
  rootMargin: "0px 0px -350px 0px",
  threshold: 0,
};

function getProductTiles({
  mightLikeProducts,
  gridEnabled,
  showDiscountPercentage,
  getTranslation,
  hasCriteoAds,
}) {
  const PriceAndTitle = gridEnabled ? PriceThenTitle : TitleThenPrice;

  return mightLikeProducts.map(
    (
      {
        url,
        productTitle,
        priceData,
        colourWayId,
        productId,
        imageUrl,
        advertisement,
      },
      index,
    ) => (
      <ProductTile
        key={url}
        url={url}
        title={productTitle}
        colourWayId={colourWayId}
        productId={productId}
        imageUrl={imageUrl}
        priceData={priceData}
        index={index}
        pageArea={PAGE_AREAS.MIGHT_LIKE}
        shouldShowTitle={true}
        productTileAriaLabel={buildProductTileAriaLabel({
          getTranslation,
          title: productTitle,
          priceData,
          showDiscountPercentage,
        })}
        advertisement={advertisement}
        showAdvertElements={hasCriteoAds}
      >
        <PriceAndTitle
          priceData={priceData}
          title={productTitle}
          showAdvertElements={hasCriteoAds}
        />
      </ProductTile>
    ),
  );
}

function handleFireMightLikeVisibleAnalytics(intersectionData) {
  if (intersectionData.isIntersecting) {
    fireMightLikeVisibleAnalytics();
  }
}

function shouldNotRender({ gridEnabled, mightLikeProducts }) {
  return gridEnabled === null || !mightLikeProducts?.length;
}

function renderContent({
  gridEnabled,
  mightLikeProducts,
  showDiscountPercentage,
  getTranslation,
  viewportCategory,
  hasCriteoAds,
}) {
  if (gridEnabled) {
    return (
      <Grid ariaDescribedby="mightLikeTitle" gridTestId="mightLikeGrid">
        {getProductTiles({
          mightLikeProducts,
          gridEnabled,
          showDiscountPercentage,
          getTranslation,
          hasCriteoAds,
        })}
      </Grid>
    );
  }

  return (
    <Carousel
      {...getDefaultCarouselSettings({
        getTranslation,
        viewportCategory,
        CarouselMode,
      })}
      ariaDescribedby="mightLikeTitle"
      carouselTestId="mightLikeCarousel"
    >
      {getProductTiles({
        mightLikeProducts,
        gridEnabled,
        showDiscountPercentage,
        getTranslation,
        hasCriteoAds,
      })}
    </Carousel>
  );
}

export default function YouMightAlsoLike({ forceGrid = false }) {
  const { product, getTranslation } = useRuntimeConstants();
  const { mightLikeProducts, mightLikeProductsWithAdverts, hasCriteoAds } =
    useRecommendations(
      {
        productId: product?.id,
      },
      true,
    );
  const productsToDisplay = hasCriteoAds
    ? mightLikeProductsWithAdverts
    : mightLikeProducts;

  const { isInStock } = useIsInStockContext();
  const { gridEnabled: defaultGridEnabled } = useGrid({ isInStock });
  const gridEnabled = forceGrid ? true : defaultGridEnabled;
  const { showDiscountPercentage } = useDiscountPercentage();
  const { viewportCategory } = useViewport();
  const observer = useRef(null);
  const element = useRef(null);
  const lastTrackedProductsKey = useRef(null);

  const shouldNotRenderRecommendations = shouldNotRender({
    gridEnabled,
    mightLikeProducts: productsToDisplay,
  });
  const marketingListName = isInStock ? "pdp_ymal" : "pdp_sml";
  const marketingProductsKey = `${marketingListName}:${(productsToDisplay || [])
    .map((product) => product.productId ?? product.id ?? product.url)
    .join(",")}`;

  const trackingRef = useCallback((node) => {
    if (node) {
      element.current = node;
      observer.current = setIntersectionObserver({
        element: element.current,
        options: INTERSECTION_OPTIONS,
        handleIntersectionData: handleFireMightLikeVisibleAnalytics,
        observeIntersectionOnce: true,
      });
    }
  }, []);

  useEffect(() => {
    return () => {
      unsetIntersectionObserver(element.current, observer.current);
    };
  }, []);

  useEffect(() => {
    if (shouldNotRenderRecommendations) {
      lastTrackedProductsKey.current = null;
      return;
    }

    if (lastTrackedProductsKey.current === marketingProductsKey) {
      return;
    }

    fireViewItemListMarketing({
      products: productsToDisplay,
      listName: marketingListName,
    });
    lastTrackedProductsKey.current = marketingProductsKey;
  }, [
    shouldNotRenderRecommendations,
    marketingListName,
    marketingProductsKey,
    productsToDisplay,
  ]);

  if (shouldNotRenderRecommendations) {
    return null;
  }

  return (
    <MightLike
      id={"mightLike"}
      title={
        isInStock
          ? getTranslation("pdp_carousel_recommendations_title")
          : getTranslation("pdp_carousel_recommendations_out_of_stock")
      }
      titleAriaLabel={getRecommendationsTitleAriaLabel({
        getTranslation,
        gridEnabled,
        isInStock,
      })}
      trackingRef={trackingRef}
      type={
        gridEnabled ? RECOMMENDATION_TYPES.GRID : RECOMMENDATION_TYPES.CAROUSEL
      }
    >
      {renderContent({
        gridEnabled,
        mightLikeProducts: productsToDisplay,
        showDiscountPercentage,
        getTranslation,
        viewportCategory,
        hasCriteoAds,
      })}
    </MightLike>
  );
}

YouMightAlsoLike.propTypes = {
  forceGrid: PropTypes.bool,
};
